import { AfterViewInit, ContentChild, Directive, Host, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { Icons } from '@app/shared/constants';
import { FieldOption } from '@app/shared/models/field.interface';
import { Subject, Subscription } from 'rxjs';
import { DataTableNewComponent } from '../data-table-new.component';
import { DataTableState } from '../state/state.model';
import { StateService } from '../state/state.service';

export interface DataTableSortState {
  column: string;
  direction: SortDirection;
}

@Directive({ selector: '[vfsDatatableSort]', exportAs: 'vfsDatatableSort' })
export class DatatableSortDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() sortColumsForCardView: FieldOption[];
  @ContentChild(MatSort, { static: true }) matSort: MatSort;

  stateId: string;
  stateChangeSubject$: Subject<DataTableState>;
  matSortSubscription: Subscription;
  cardSortSelectSubscription: Subscription;
  cardSortButtonSubscription: Subscription;

  constructor(
    @Host() private parent: DataTableNewComponent<any>,
    @Inject('StateService') private stateService: StateService
  ) {
    this.parent.withSort = true;
  }

  ngOnInit() {
    this.stateId = this.parent.stateId;
    this.stateChangeSubject$ = this.parent.stateChangeSubject$;
  }

  ngAfterViewInit(): void {
    const state: DataTableState = this.stateService.get(this.stateId);

    if (this.matSort) {
      this.initializeTableSort(state);
    }

    if (this.parent.sortSelect && this.parent.sortButton) {
      this.initializeCardSort(state);
      this.parent.sortSelect.value = state.sort.column;
    }
  }

  ngOnDestroy() {
    if (this.matSortSubscription) {
      this.matSortSubscription.unsubscribe();
    }

    if (this.cardSortButtonSubscription) {
      this.cardSortButtonSubscription.unsubscribe();
    }

    if (this.cardSortSelectSubscription) {
      this.cardSortSelectSubscription.unsubscribe();
    }
  }

  private initializeTableSort(state: DataTableState): void {
    this.matSort.active = state.sort.column;
    this.matSort.direction = state.sort.direction;

    this.matSortSubscription = this.matSort.sortChange.subscribe((sort: Sort) => {
      const currentState: DataTableState = this.stateService.get(this.stateId);
      currentState.sort.column = sort.active;
      currentState.sort.direction = sort.direction;
      if (this.parent.sortSelect && this.parent.sortButton) {
        this.parent.sortSelect.value = sort.active;
        this.parent.sortButton.icon = state.sort.direction === 'asc' ? Icons.SORT_ASC : Icons.SORT_DESC;
        this.parent.sortIcon = state.sort.direction === 'asc' ? Icons.SORT_ASC : Icons.SORT_DESC;
      }

      this.stateService.update(currentState);

      this.stateChangeSubject$.next(currentState);
    });
  }
  // FIXME: If list view only, this matSort doesn't exist, (undefined error)
  private initializeCardSort(state: DataTableState): void {
    // Card sort button
    this.parent.sortButton.icon = state.sort.direction === 'asc' ? Icons.SORT_ASC : Icons.SORT_DESC;
    this.cardSortButtonSubscription = this.parent.sortButton.buttonClick.subscribe(_ => {
      const currentState: DataTableState = this.stateService.get(this.stateId);
      if (this.parent.sortIcon === Icons.SORT_DESC) {
        this.parent.sortIcon = Icons.SORT_ASC;
        currentState.sort.direction = 'asc';
      } else {
        this.parent.sortIcon = Icons.SORT_DESC;
        currentState.sort.direction = 'desc';
      }

      this.stateService.update(currentState);

      // sync view in case matsort is present
      if (this.matSort) {
        this.matSort.direction = currentState.sort.direction;
        this.matSort._stateChanges.next(); // this is necessary to update the UI
      }

      this.stateChangeSubject$.next(currentState);
    });

    // Card sort select
    this.parent.sortSelect.options = this.sortColumsForCardView;
    this.cardSortSelectSubscription = this.parent.sortSelect.changeValue.subscribe(column => {
      const currentState: DataTableState = this.stateService.get(this.stateId);
      currentState.sort.column = column;

      this.stateService.update(currentState);

      // sync view in case matsort is present
      if (this.matSort) {
        this.matSort.active = column;
        this.matSort._stateChanges.next(); // this is necessary to update the UI
      }

      this.stateChangeSubject$.next(currentState);
    });
  }
}
